/* ==========================================================================
   Hero Cards — <hero-cards> Custom Element
   Scatter layout + auto-rotating spotlight. Instance state lives on the
   element itself, so Shopify's section:load DOM replacement is handled by
   the connectedCallback/disconnectedCallback lifecycle automatically.
   ========================================================================== */

class HeroCards extends HTMLElement {
  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    this.stopLoop();
  }

  stopLoop() {
    if (this.loopController) {
      this.loopController.stop();
      this.loopController = null;
    }
  }

  render() {
    const sectionId = this.id.replace('hero-', '');
    const dataEl = document.getElementById('hero-cards-data-' + sectionId);
    const scatterEl = this.querySelector('.hero-cards__scatter');
    if (!dataEl || !scatterEl) return;

    let allCards;
    try {
      allCards = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!allCards || allCards.length === 0) return;

    this.stopLoop();
    scatterEl.innerHTML = '';
    scatterEl.classList.remove('design-mode-grid');

    // Design mode: show all cards in a grid for admin editing
    if (window.Shopify && window.Shopify.designMode) {
      scatterEl.classList.add('design-mode-grid');
      allCards.forEach((card) => scatterEl.appendChild(createCardEl(card)));
      return;
    }

    const isMobile = window.innerWidth < 750;
    const positions = scatterPositions(allCards.length, scatterEl, isMobile);

    allCards.forEach((card, i) => {
      const pos = positions[i];
      const el = createCardEl(card);
      el.style.left = pos.x + '%';
      el.style.top = pos.y + '%';
      el.style.setProperty('--card-rotation', pos.rot + 'deg');
      el.classList.add('is-flipped');
      el.style.zIndex = i + 1;
      scatterEl.appendChild(el);
    });

    this.loopController = startSpotlightLoop(scatterEl);
  }
}

customElements.define('hero-cards', HeroCards);

/* --- Card DOM Creation --- */

function createCardEl(card) {
  const el = document.createElement('div');
  el.className = 'hero-cards__card';
  el.setAttribute('data-block-id', card.id);

  const inner = document.createElement('div');
  inner.className = 'hero-cards__card-inner';

  const front = document.createElement('div');
  front.className = 'hero-cards__card-front';
  const text = document.createElement('p');
  text.className = 'hero-cards__card-text';
  text.textContent = card.text;
  front.appendChild(text);

  const back = document.createElement('div');
  back.className = 'hero-cards__card-back';

  inner.appendChild(front);
  inner.appendChild(back);
  el.appendChild(inner);
  return el;
}

/* --- Scatter Positioning (avoids vertical center band) --- */

function scatterPositions(count, container, isMobile) {
  const rect = container.getBoundingClientRect();
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

  // Card size in rem (must match CSS)
  let cardWRem, cardHRem;
  if (isMobile) { cardWRem = 22; cardHRem = 16; }
  else if (window.innerWidth < 990) { cardWRem = 28; cardHRem = 20; }
  else { cardWRem = 30; cardHRem = 22; }

  const w = rect.width || window.innerWidth;
  const h = rect.height || window.innerHeight;
  const cardWpct = (cardWRem * rootFontSize / w) * 100;
  const cardHpct = (cardHRem * rootFontSize / h) * 100;

  // Allow cards to bleed well over the edge — "lying at the rim" feel.
  // Anchor is card center; on mobile we force a hard left/right lean via buckets.
  const padX = isMobile ? -10 : 6;
  const padY = isMobile ? 6 : 4;
  const minX = padX, maxX = 100 - padX;
  const minY = padY, maxY = 100 - padY;

  const mobileLeftMax = 25;
  const mobileRightMin = 75;

  // Central clear zone — derived from card size so card edges never enter it
  const coreYMin = 40;
  const coreYMax = isMobile ? 68 : 60;
  const forbidYMin = coreYMin - cardHpct / 2 - 1;
  const forbidYMax = coreYMax + cardHpct / 2 + 1;

  let forbidXMin = -1;
  let forbidXMax = -1;
  if (!isMobile && cardWpct < 50) {
    const coreXMin = 36;
    const coreXMax = 64;
    forbidXMin = coreXMin - cardWpct / 2 - 1;
    forbidXMax = coreXMax + cardWpct / 2 + 1;
  }

  const minDist = Math.max(isMobile ? 16 : 14, Math.min(cardWpct, cardHpct) * 0.55);

  const positions = [];
  let attempts = 0;

  while (positions.length < count && attempts < 800) {
    attempts++;
    let x;
    if (isMobile) {
      const leftSide = Math.random() < 0.5;
      x = leftSide
        ? minX + Math.random() * (mobileLeftMax - minX)
        : mobileRightMin + Math.random() * (maxX - mobileRightMin);
    } else {
      x = minX + Math.random() * (maxX - minX);
    }
    const y = minY + Math.random() * (maxY - minY);

    if (y > forbidYMin && y < forbidYMax) continue;
    if (forbidXMin >= 0 && x > forbidXMin && x < forbidXMax) continue;

    let ok = true;
    for (let j = 0; j < positions.length; j++) {
      const dx = positions[j].x - x;
      const dy = positions[j].y - y;
      if (Math.sqrt(dx * dx + dy * dy) < minDist) { ok = false; break; }
    }
    if (!ok) continue;

    const rot = (Math.random() - 0.5) * 36;
    positions.push({ x: x, y: y, rot: Math.round(rot * 10) / 10 });
  }

  // Fallback: place remaining cards deterministically at edges
  const fallbackAnchors = isMobile ? [
    { x: -5, y: 10 }, { x: 105, y: 12 },
    { x: 0, y: 90 }, { x: 100, y: 88 },
    { x: -8, y: 25 }, { x: 108, y: 78 }
  ] : [
    { x: 10, y: 8 }, { x: 90, y: 10 },
    { x: 15, y: 92 }, { x: 85, y: 90 },
    { x: 8, y: 40 }, { x: 92, y: 40 }
  ];
  let fi = 0;
  while (positions.length < count && fi < fallbackAnchors.length) {
    const a = fallbackAnchors[fi++];
    if (a.y > forbidYMin && a.y < forbidYMax) continue;
    if (forbidXMin >= 0 && a.x > forbidXMin && a.x < forbidXMax) continue;
    positions.push({
      x: a.x,
      y: a.y,
      rot: Math.round((Math.random() - 0.5) * 36 * 10) / 10
    });
  }
  while (positions.length < count) {
    let xFinal;
    if (isMobile) {
      xFinal = Math.random() < 0.5
        ? minX + Math.random() * (mobileLeftMax - minX)
        : mobileRightMin + Math.random() * (maxX - mobileRightMin);
    } else {
      xFinal = minX + Math.random() * (maxX - minX);
    }
    positions.push({
      x: xFinal,
      y: Math.random() < 0.5 ? minY : maxY,
      rot: Math.round((Math.random() - 0.5) * 36 * 10) / 10
    });
  }

  return positions;
}

/* --- Spotlight Loop --- */

function startSpotlightLoop(scatterEl) {
  const MOVE_MS = 700;
  const FLIP_MS = 600;
  const DWELL_MS = 6500;
  const GAP_MS = 300;

  const prefersReducedMotion = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReducedMotion) return { stop: function () {} };

  let nextIdx = 0;
  let zCounter = 100;
  let running = true;
  let cycleActive = false;
  let timeouts = [];

  function track(id) { timeouts.push(id); return id; }

  function clearAll() {
    for (let i = 0; i < timeouts.length; i++) clearTimeout(timeouts[i]);
    timeouts = [];
  }

  function resetSpotlightCards() {
    const active = scatterEl.querySelectorAll('.hero-cards__card.is-spotlight');
    for (let i = 0; i < active.length; i++) {
      active[i].classList.add('is-flipped');
      active[i].classList.remove('is-spotlight');
    }
  }

  function onVisibilityChange() {
    if (document.hidden) {
      running = false;
      clearAll();
      resetSpotlightCards();
      cycleActive = false;
    } else if (!running) {
      running = true;
      scheduleNext(500);
    }
  }
  document.addEventListener('visibilitychange', onVisibilityChange);

  function tick() {
    if (!running || !scatterEl.isConnected || cycleActive) return;

    const cards = scatterEl.querySelectorAll('.hero-cards__card');
    if (!cards.length) return;

    cycleActive = true;

    const idx = nextIdx % cards.length;
    nextIdx = (idx + 1) % cards.length;

    const card = cards[idx];
    card.classList.add('is-spotlight');

    // After move completes: rise to top layer, then flip face-up
    track(setTimeout(function () {
      zCounter++;
      card.style.zIndex = zCounter;
      card.classList.remove('is-flipped');
    }, MOVE_MS));

    // After dwell, flip back face-down
    track(setTimeout(function () {
      card.classList.add('is-flipped');
    }, MOVE_MS + FLIP_MS + DWELL_MS));

    // After back-flip, return to scatter position and free the cycle lock
    track(setTimeout(function () {
      card.classList.remove('is-spotlight');
      cycleActive = false;
    }, MOVE_MS + FLIP_MS + DWELL_MS + FLIP_MS));

    scheduleNext(MOVE_MS + FLIP_MS + DWELL_MS + FLIP_MS + MOVE_MS + GAP_MS);
  }

  function scheduleNext(delay) {
    track(setTimeout(tick, delay));
  }

  scheduleNext(900);

  return {
    stop: function () {
      running = false;
      clearAll();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      resetSpotlightCards();
      cycleActive = false;
    }
  };
}

/* --- Hero Offset (header height) — window-level, shared across all heroes --- */

function updateHeroOffset() {
  const header = document.querySelector('.shopify-section-group-header-group')
    || document.querySelector('.header-wrapper')
    || document.querySelector('sticky-header')
    || document.querySelector('header.header');
  if (!header) return;
  const h = header.getBoundingClientRect().height;
  if (h > 0) {
    document.documentElement.style.setProperty('--hero-offset', h + 'px');
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateHeroOffset);
} else {
  updateHeroOffset();
}
window.addEventListener('load', updateHeroOffset);
window.addEventListener('resize', updateHeroOffset);
